/**
 * Wishlist機能
 * LocalStorage使用、キー名: wishlistItem
 */

class Wishlist {
  constructor() {
    this.storageKey = 'wishlistItem';
    this.init();
  }

  init() {
    // ページ読み込み時の初期化
    this.updateUI();
    this.bindEvents();
  }

  // LocalStorageから取得
  getWishlist() {
    const items = localStorage.getItem(this.storageKey);
    return items ? JSON.parse(items) : [];
  }

  // LocalStorageに保存
  saveWishlist(items) {
    localStorage.setItem(this.storageKey, JSON.stringify(items));
    this.updateUI();
  }

  // 商品を追加
  addItem(handle) {
    const wishlist = this.getWishlist();
    if (!wishlist.includes(handle)) {
      wishlist.push(handle);
      this.saveWishlist(wishlist);
      return true;
    }
    return false;
  }

  // 商品を削除
  removeItem(handle) {
    let wishlist = this.getWishlist();
    wishlist = wishlist.filter(item => item !== handle);
    this.saveWishlist(wishlist);
  }

  // 商品が含まれているか確認
  hasItem(handle) {
    return this.getWishlist().includes(handle);
  }

  // 全てのwishlistボタンのUI更新
  updateUI() {
    const buttons = document.querySelectorAll('[data-wishlist-handle]');
    const wishlist = this.getWishlist();
    
    buttons.forEach(button => {
      const handle = button.dataset.wishlistHandle;
      if (wishlist.includes(handle)) {
        button.classList.add('added');
      } else {
        button.classList.remove('added');
      }
    });

    // カウント更新
    this.updateCount();
  }

  // wishlistカウント更新
  updateCount() {
    const count = this.getWishlist().length;
    const counters = document.querySelectorAll('[data-wishlist-count]');
    counters.forEach(counter => {
      // カウントが0の時は空文字列（非表示）、それ以外は数字を表示
      counter.textContent = count > 0 ? count : '';
    });
  }

  // イベントバインド
  bindEvents() {
    // wishlistボタンのクリック
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-wishlist-handle]');
      if (!button) return;

      e.preventDefault();

      // ログインチェック
      if (!window.isCustomerLoggedIn) {
        this.showLoginModal();
        return;
      }

      const handle = button.dataset.wishlistHandle;

      if (this.hasItem(handle)) {
        this.removeItem(handle);
      } else {
        this.addItem(handle);
      }
    });

    // ヘッダーのお気に入りアイコン（未ログイン時）
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-wishlist-login-trigger]');
      if (!trigger) return;

      e.preventDefault();
      this.showLoginModal();
    });
  }

  // ログインモーダル表示
  showLoginModal() {
    const modal = document.getElementById('wishlist-login-modal');
    if (!modal) return;

    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    // 閉じるボタン
    const closeBtn = modal.querySelector('.wishlist-login-modal__close');
    const overlay = modal.querySelector('.wishlist-login-modal__overlay');

    // オーバーレイにis-visibleクラスを付与
    if (overlay) {
      setTimeout(() => {
        overlay.classList.add('is-visible');
      }, 10);
    }

    const closeModal = () => {
      // オーバーレイのis-visibleクラスを削除
      if (overlay) {
        overlay.classList.remove('is-visible');
      }

      // トランジション後にモーダルを非表示
      setTimeout(() => {
        modal.hidden = true;
        document.body.style.overflow = '';
      }, 300);
    };

    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal, { once: true });
    }

    if (overlay) {
      overlay.addEventListener('click', closeModal, { once: true });
    }
  }

  // wishlistページ: 商品データを取得してHTML生成
  async loadWishlistPage() {
    const wishlist = this.getWishlist();
    const container = document.querySelector('[data-wishlist-container]');
    
    if (!container) return;

    if (wishlist.length === 0) {
      this.showEmptyMessage(container);
      return;
    }

    container.innerHTML = '<div class="loading">読み込み中...</div>';

    try {
      const products = await this.fetchProducts(wishlist);
      this.renderProducts(container, products);
    } catch (error) {
      console.error('Wishlist load error:', error);
      container.innerHTML = '<p class="error">商品の読み込みに失敗しました</p>';
    }
  }

  // 商品データを取得
  async fetchProducts(handles) {
    const products = [];
    
    for (const handle of handles) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const product = await response.json();
          products.push(product);
        }
      } catch (error) {
        console.error(`Failed to fetch product: ${handle}`, error);
      }
    }
    
    return products;
  }

  // 商品をレンダリング
  renderProducts(container, products) {
    const grid = document.createElement('div');
    grid.className = 'wishlist-grid';

    products.forEach(product => {
      const item = this.createProductCard(product);
      grid.appendChild(item);
    });

    container.innerHTML = '';
    container.appendChild(grid);
  }

  // 商品カードを作成
  createProductCard(product) {
    const div = document.createElement('div');
    div.className = 'wishlist-item';
    div.dataset.productHandle = product.handle;

    const price = product.price / 100;
    const comparePrice = product.compare_at_price ? product.compare_at_price / 100 : null;
    const image = product.images[0] || '';

    div.innerHTML = `
      <div class="wishlist-item__image">
        <a href="/products/${product.handle}">
          <img src="${image}" alt="${product.title}" loading="lazy">
        </a>
      </div>
      <div class="wishlist-item__info">
        <h3 class="wishlist-item__title">
          <a href="/products/${product.handle}">${product.title}</a>
        </h3>
        <div class="wishlist-item__price">
          ${comparePrice ? `<s class="price--compare">¥${comparePrice.toLocaleString()}</s>` : ''}
          <span class="price--sale">¥${price.toLocaleString()}</span>
        </div>
        <div class="wishlist-item__actions">
          <button type="button" 
                  class="button button--primary" 
                  data-add-to-cart="${product.variants[0].id}">
            カートに追加
          </button>
          <button type="button" 
                  class="button button--secondary" 
                  data-wishlist-remove="${product.handle}">
            削除
          </button>
        </div>
      </div>
    `;

    return div;
  }

  // 空のメッセージ表示
  showEmptyMessage(container) {
    container.innerHTML = `
      <div class="wishlist-empty">
        <p class="wishlist-empty__text">お気に入りに商品がありません</p>
        <a href="/collections/all" class="button button--primary">
          ショッピングを続ける
        </a>
      </div>
    `;
  }
}

// 初期化
document.addEventListener('DOMContentLoaded', () => {
  window.wishlist = new Wishlist();

  // wishlistページの場合、商品を読み込む
  if (document.querySelector('[data-wishlist-container]')) {
    window.wishlist.loadWishlistPage();
  }
});

// wishlistから削除
document.addEventListener('click', (e) => {
  const removeBtn = e.target.closest('[data-wishlist-remove]');
  if (removeBtn) {
    const handle = removeBtn.dataset.wishlistRemove;
    window.wishlist.removeItem(handle);
    
    // DOM要素を削除
    const item = removeBtn.closest('.wishlist-item');
    if (item) {
      item.remove();
    }

    // 空になったらメッセージ表示
    if (window.wishlist.getWishlist().length === 0) {
      const container = document.querySelector('[data-wishlist-container]');
      window.wishlist.showEmptyMessage(container);
    }
  }
});

// カートに追加
document.addEventListener('click', async (e) => {
  const addBtn = e.target.closest('[data-add-to-cart]');
  if (!addBtn) return;

  const variantId = addBtn.dataset.addToCart;
  addBtn.disabled = true;
  addBtn.textContent = '追加中...';

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    if (response.ok) {
      addBtn.textContent = '追加しました';
      
      // カート数を更新
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      document.querySelectorAll('[data-cart-count]').forEach(counter => {
        counter.textContent = cart.item_count;
      });

      setTimeout(() => {
        addBtn.disabled = false;
        addBtn.textContent = 'カートに追加';
      }, 2000);
    }
  } catch (error) {
    console.error('Add to cart error:', error);
    addBtn.disabled = false;
    addBtn.textContent = 'エラー';
  }
});
